/**
 * Calendar arithmetic, kept out of the component.
 *
 * Not for tidiness: Node can strip TypeScript types but not JSX, so a `.tsx`
 * module cannot be imported by the test runner at all. All of this is the kind
 * of arithmetic that is wrong by one somewhere and right everywhere else -
 * exactly what needs a test - so it lives where a test can reach it.
 *
 * Dates are local Y/M/D numbers throughout, never UTC. A calendar cell is a day
 * in the reader's timezone; going through `Date.UTC` or `toISOString()` shifts
 * it by nine hours in Japan and renders the first and last day of every month
 * in the wrong one.
 */
export const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

/** Local Y-M-D, never UTC. See the note above. */
export const iso = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

/** ISO date to a local Date at midnight, or null for anything else. */
export const parseIso = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  // Rejects 2026-02-31, which the constructor would roll into March.
  return date.getMonth() === Number(m) - 1 ? date : null;
};

/**
 * What somebody typed, as a date.
 *
 * Slashes and dots as well as hyphens, and a bare 11/7 meaning this year,
 * because those are what people actually type into a date box. Anything else
 * is left alone rather than guessed at.
 */
export const parseTyped = (value: string, today: Date): Date | null => {
  const text = value.trim().replace(/[./年月]/g, '-').replace(/日$/, '');
  const full = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (full) {
    return parseIso(
      `${full[1]}-${full[2]!.padStart(2, '0')}-${full[3]!.padStart(2, '0')}`,
    );
  }
  const short = /^(\d{1,2})-(\d{1,2})$/.exec(text);
  if (short) {
    return parseIso(
      `${today.getFullYear()}-${short[1]!.padStart(2, '0')}-${short[2]!.padStart(2, '0')}`,
    );
  }
  return null;
};

export const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** Whole months, clamped: 1月31日 plus one month is 2月28日, not 3月3日. */
export const addMonths = (date: Date, months: number): Date => {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), lastDay));
};

/** The six weeks a month grid shows, so the popup never changes height. */
export const monthGrid = (month: Date): Date[] => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = addDays(first, -first.getDay());
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
};

